import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTAddressToSDKAddress } from '@wix/sdk-runtime/transformations/address';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTPageURLToSDKPageURL } from '@wix/sdk-runtime/transformations/page-url';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkCancelEventsByFilterOptions,
  BulkDeleteEventsByFilterOptions,
  CancelEventApplicationErrors,
  CancelEventOptions,
  CancelEventResponse,
  CloneEventOptions,
  CloneEventResponse,
  CountEventsByStatusApplicationErrors,
  CountEventsByStatusOptions,
  CountEventsByStatusResponse,
  CreateEventApplicationErrors,
  CreateEventOptions,
  CreateEventValidationErrors,
  DeleteEventResponse,
  Event,
  EventCanceledEnvelope,
  EventClonedEnvelope,
  EventCreatedEnvelope,
  EventDeletedEnvelope,
  EventEndedEnvelope,
  EventPublishedEnvelope,
  EventQuery,
  EventReminderEnvelope,
  EventStartedEnvelope,
  EventUpdatedEnvelope,
  EventsQueryBuilder,
  GetEventBySlugOptions,
  GetEventBySlugResponse,
  GetEventOptions,
  ListEventsByCategoryOptions,
  ListEventsByCategoryResponse,
  PublishDraftEventApplicationErrors,
  PublishDraftEventOptions,
  PublishDraftEventResponse,
  QueryEventsApplicationErrors,
  QueryEventsOptions,
  QueryEventsResponse,
  UpdateEventApplicationErrors,
  UpdateEventOptions,
  UpdateEventValidationErrors,
  bulkCancelEventsByFilter as universalBulkCancelEventsByFilter,
  bulkDeleteEventsByFilter as universalBulkDeleteEventsByFilter,
  cancelEvent as universalCancelEvent,
  cloneEvent as universalCloneEvent,
  countEventsByStatus as universalCountEventsByStatus,
  createEvent as universalCreateEvent,
  deleteEvent as universalDeleteEvent,
  getEvent as universalGetEvent,
  getEventBySlug as universalGetEventBySlug,
  listEventsByCategory as universalListEventsByCategory,
  publishDraftEvent as universalPublishDraftEvent,
  queryEvents as universalQueryEvents,
  typedQueryEvents as universalTypedQueryEvents,
  updateEvent as universalUpdateEvent,
} from './events-v3-event-wix-events-v-2.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/events' };

export function createEvent(httpClient: HttpClient): CreateEventSignature {
  return (
    event: NonNullablePaths<
      Event,
      `dateAndTimeSettings` | `location` | `registration.initialType` | `title`,
      3
    >,
    options?: CreateEventOptions
  ) =>
    universalCreateEvent(
      event,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateEventSignature {
  /**
   * Creates an event. <br> <br>
   * The event includes a default registration form in the selected language, which consists of input fields for first name, last name, and email. See the [Registration Form API](https://dev.wix.com/docs/rest/business-solutions/events/form/about-the-registration-form-api) for more information. <br> <br>
   * You can create the event as a draft by setting `draft` to `true`. Otherwise, the event is published right away. <br> <br>
   * To create a recurring event, set `individualEventDates` in the `dateAndTimeSettings.recurringEvents` object. <br> <br>
   * The event is automatically set up to send daily summary reports of new guests to your business email.
   * @param - Event data.
   * @param - Optional fields.
   * @returns Created event.
   */
  (
    event: NonNullablePaths<
      Event,
      `dateAndTimeSettings` | `location` | `registration.initialType` | `title`,
      3
    >,
    options?: CreateEventOptions
  ): Promise<
    NonNullablePaths<
      Event,
      | `_id`
      | `location.type`
      | `location.address.streetAddress.number`
      | `location.address.streetAddress.name`
      | `dateAndTimeSettings.recurrenceStatus`
      | `dateAndTimeSettings.recurringEvents.individualEventDates`
      | `dateAndTimeSettings.recurringEvents.individualEventDates.${number}.showTimeZone`
      | `slug`
      | `status`
      | `registration.type`
      | `registration.status`
      | `registration.rsvp.responseType`
      | `registration.rsvp.waitlistEnabled`
      | `registration.tickets.guestsAssignedSeparately`
      | `registration.tickets.ticketLimitPerOrder`
      | `registration.tickets.lowestPrice.value`
      | `registration.tickets.lowestPrice.currency`
      | `registration.tickets.taxSettings.type`
      | `registration.allowedGuestTypes`
      | `registration.initialType`
      | `registration.registrationPaused`
      | `registration.registrationDisabled`
      | `calendarUrls.google`
      | `calendarUrls.ics`
      | `form.controls`
      | `form.controls.${number}.type`
      | `form.controls.${number}.system`
      | `form.controls.${number}.name`
      | `form.controls.${number}.label`
      | `form.controls.${number}.orderIndex`
      | `form.controls.${number}._id`
      | `form.messages.rsvp.rsvpYesOption`
      | `form.messages.rsvp.rsvpNoOption`
      | `form.messages.rsvp.positiveMessages.title`
      | `form.messages.rsvp.positiveMessages.confirmation.title`
      | `form.messages.rsvp.positiveMessages.confirmation.message`
      | `form.messages.rsvp.positiveMessages.confirmation.addToCalendarActionLabel`
      | `form.messages.rsvp.positiveMessages.confirmation.shareActionLabel`
      | `form.messages.rsvp.negativeMessages.title`
      | `form.messages.rsvp.negativeMessages.confirmation.title`
      | `form.messages.rsvp.negativeMessages.confirmation.shareActionLabel`
      | `form.messages.rsvp.submitActionLabel`
      | `form.messages.checkout.title`
      | `form.messages.checkout.submitActionLabel`
      | `form.messages.checkout.confirmation.title`
      | `form.messages.checkout.confirmation.message`
      | `form.messages.checkout.confirmation.downloadTicketsLabel`
      | `form.messages.checkout.confirmation.addToCalendarLabel`
      | `form.messages.checkout.confirmation.shareEventLabel`
      | `form.messages.registrationClosed.message`
      | `form.messages.registrationClosed.exploreEventsActionLabel`
      | `form.messages.ticketsUnavailable.message`
      | `form.messages.ticketsUnavailable.exploreEventsActionLabel`
      | `summaries.rsvps.totalCount`
      | `summaries.rsvps.yesCount`
      | `summaries.rsvps.noCount`
      | `summaries.rsvps.waitlistCount`
      | `summaries.tickets.ticketsSold`
      | `summaries.tickets.revenue.amount`
      | `summaries.tickets.revenue.currency`
      | `summaries.tickets.currencyLocked`
      | `summaries.tickets.totalOrders`
      | `instanceId`
      | `guestListSettings.displayedPublicly`
      | `userId`
      | `onlineConferencing.enabled`
      | `onlineConferencing.type`
      | `onlineConferencing.session.hostLink`
      | `onlineConferencing.session.guestLink`
      | `seoSettings.slug`
      | `seoSettings.advancedSeoData.tags`
      | `seoSettings.advancedSeoData.tags.${number}.type`
      | `seoSettings.advancedSeoData.tags.${number}.children`
      | `seoSettings.advancedSeoData.tags.${number}.custom`
      | `seoSettings.advancedSeoData.tags.${number}.disabled`
      | `seoSettings.advancedSeoData.settings.preventAutoRedirect`
      | `seoSettings.advancedSeoData.settings.keywords`
      | `seoSettings.advancedSeoData.settings.keywords.${number}.term`
      | `seoSettings.advancedSeoData.settings.keywords.${number}.isMain`
      | `agendaSettings.enabled`,
      7
    > & {
      __applicationErrorsType?: CreateEventApplicationErrors;
      __validationErrorsType?: CreateEventValidationErrors;
    }
  >;
}

export function cloneEvent(httpClient: HttpClient): CloneEventSignature {
  return (eventId: string, options?: CloneEventOptions) =>
    universalCloneEvent(
      eventId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CloneEventSignature {
  /**
   * Clones an event, including the registration form, notifications, multilingual translations and ticket configuration from the original event. <br> <br>
   * The new event's date is automatically set to 14 days from the original event date. <br> <br>
   * If an event with the same `title` already exists, the new event's `title` gets a sequence number. For example, if you clone an event named `"Leather Crafting 101"`, the new event's title is `"Leather Crafting 101 (1)"`. <br> <br>
   * You can change the required entity field values while cloning an event.
   * @param - Event ID.
   * @param - Optional fields.
   */
  (eventId: string, options?: CloneEventOptions): Promise<
    NonNullablePaths<
      CloneEventResponse,
      | `event._id`
      | `event.location.type`
      | `event.location.address.streetAddress.number`
      | `event.location.address.streetAddress.name`
      | `event.dateAndTimeSettings.recurrenceStatus`
      | `event.dateAndTimeSettings.recurringEvents.individualEventDates`
      | `event.dateAndTimeSettings.recurringEvents.individualEventDates.${number}.showTimeZone`
      | `event.slug`
      | `event.status`
      | `event.registration.type`
      | `event.registration.status`
      | `event.registration.rsvp.responseType`
      | `event.registration.rsvp.waitlistEnabled`
      | `event.registration.tickets.guestsAssignedSeparately`
      | `event.registration.tickets.ticketLimitPerOrder`
      | `event.registration.tickets.lowestPrice.value`
      | `event.registration.tickets.lowestPrice.currency`
      | `event.registration.tickets.taxSettings.type`
      | `event.registration.allowedGuestTypes`
      | `event.registration.initialType`
      | `event.registration.registrationPaused`
      | `event.registration.registrationDisabled`
      | `event.calendarUrls.google`
      | `event.calendarUrls.ics`
      | `event.form.controls`
      | `event.form.controls.${number}.type`
      | `event.form.controls.${number}.system`
      | `event.form.controls.${number}.name`
      | `event.form.controls.${number}.label`
      | `event.form.controls.${number}.orderIndex`
      | `event.form.controls.${number}._id`
      | `event.form.messages.rsvp.rsvpYesOption`
      | `event.form.messages.rsvp.rsvpNoOption`
      | `event.form.messages.rsvp.positiveMessages.title`
      | `event.form.messages.rsvp.positiveMessages.confirmation.title`
      | `event.form.messages.rsvp.positiveMessages.confirmation.message`
      | `event.form.messages.rsvp.positiveMessages.confirmation.addToCalendarActionLabel`
      | `event.form.messages.rsvp.positiveMessages.confirmation.shareActionLabel`
      | `event.form.messages.rsvp.negativeMessages.title`
      | `event.form.messages.rsvp.negativeMessages.confirmation.title`
      | `event.form.messages.rsvp.negativeMessages.confirmation.shareActionLabel`
      | `event.form.messages.rsvp.submitActionLabel`
      | `event.form.messages.checkout.title`
      | `event.form.messages.checkout.submitActionLabel`
      | `event.form.messages.checkout.confirmation.title`
      | `event.form.messages.checkout.confirmation.message`
      | `event.form.messages.checkout.confirmation.downloadTicketsLabel`
      | `event.form.messages.checkout.confirmation.addToCalendarLabel`
      | `event.form.messages.checkout.confirmation.shareEventLabel`
      | `event.form.messages.registrationClosed.message`
      | `event.form.messages.registrationClosed.exploreEventsActionLabel`
      | `event.form.messages.ticketsUnavailable.message`
      | `event.form.messages.ticketsUnavailable.exploreEventsActionLabel`
      | `event.summaries.rsvps.totalCount`
      | `event.summaries.rsvps.yesCount`
      | `event.summaries.rsvps.noCount`
      | `event.summaries.rsvps.waitlistCount`
      | `event.summaries.tickets.ticketsSold`
      | `event.summaries.tickets.revenue.amount`
      | `event.summaries.tickets.revenue.currency`
      | `event.summaries.tickets.currencyLocked`
      | `event.summaries.tickets.totalOrders`
      | `event.instanceId`
      | `event.guestListSettings.displayedPublicly`
      | `event.userId`
      | `event.onlineConferencing.enabled`
      | `event.onlineConferencing.type`
      | `event.onlineConferencing.session.hostLink`
      | `event.onlineConferencing.session.guestLink`
      | `event.seoSettings.slug`
      | `event.seoSettings.advancedSeoData.tags`
      | `event.seoSettings.advancedSeoData.tags.${number}.type`
      | `event.seoSettings.advancedSeoData.tags.${number}.children`
      | `event.seoSettings.advancedSeoData.tags.${number}.custom`
      | `event.seoSettings.advancedSeoData.tags.${number}.disabled`
      | `event.seoSettings.advancedSeoData.settings.preventAutoRedirect`
      | `event.seoSettings.advancedSeoData.settings.keywords`
      | `event.seoSettings.advancedSeoData.settings.keywords.${number}.term`
      | `event.seoSettings.advancedSeoData.settings.keywords.${number}.isMain`
      | `event.agendaSettings.enabled`,
      8
    >
  >;
}

export function updateEvent(httpClient: HttpClient): UpdateEventSignature {
  return (_id: string, options?: UpdateEventOptions) =>
    universalUpdateEvent(
      _id,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateEventSignature {
  /**
   * Updates an event.
   * @param - Event ID.
   * @param - Optional fields.
   * @returns Updated event.
   */
  (_id: string, options?: UpdateEventOptions): Promise<
    NonNullablePaths<
      Event,
      | `_id`
      | `location.type`
      | `location.address.streetAddress.number`
      | `location.address.streetAddress.name`
      | `dateAndTimeSettings.recurrenceStatus`
      | `dateAndTimeSettings.recurringEvents.individualEventDates`
      | `dateAndTimeSettings.recurringEvents.individualEventDates.${number}.showTimeZone`
      | `slug`
      | `status`
      | `registration.type`
      | `registration.status`
      | `registration.rsvp.responseType`
      | `registration.rsvp.waitlistEnabled`
      | `registration.tickets.guestsAssignedSeparately`
      | `registration.tickets.ticketLimitPerOrder`
      | `registration.tickets.lowestPrice.value`
      | `registration.tickets.lowestPrice.currency`
      | `registration.tickets.taxSettings.type`
      | `registration.allowedGuestTypes`
      | `registration.initialType`
      | `registration.registrationPaused`
      | `registration.registrationDisabled`
      | `calendarUrls.google`
      | `calendarUrls.ics`
      | `form.controls`
      | `form.controls.${number}.type`
      | `form.controls.${number}.system`
      | `form.controls.${number}.name`
      | `form.controls.${number}.label`
      | `form.controls.${number}.orderIndex`
      | `form.controls.${number}._id`
      | `form.messages.rsvp.rsvpYesOption`
      | `form.messages.rsvp.rsvpNoOption`
      | `form.messages.rsvp.positiveMessages.title`
      | `form.messages.rsvp.positiveMessages.confirmation.title`
      | `form.messages.rsvp.positiveMessages.confirmation.message`
      | `form.messages.rsvp.positiveMessages.confirmation.addToCalendarActionLabel`
      | `form.messages.rsvp.positiveMessages.confirmation.shareActionLabel`
      | `form.messages.rsvp.negativeMessages.title`
      | `form.messages.rsvp.negativeMessages.confirmation.title`
      | `form.messages.rsvp.negativeMessages.confirmation.shareActionLabel`
      | `form.messages.rsvp.submitActionLabel`
      | `form.messages.checkout.title`
      | `form.messages.checkout.submitActionLabel`
      | `form.messages.checkout.confirmation.title`
      | `form.messages.checkout.confirmation.message`
      | `form.messages.checkout.confirmation.downloadTicketsLabel`
      | `form.messages.checkout.confirmation.addToCalendarLabel`
      | `form.messages.checkout.confirmation.shareEventLabel`
      | `form.messages.registrationClosed.message`
      | `form.messages.registrationClosed.exploreEventsActionLabel`
      | `form.messages.ticketsUnavailable.message`
      | `form.messages.ticketsUnavailable.exploreEventsActionLabel`
      | `summaries.rsvps.totalCount`
      | `summaries.rsvps.yesCount`
      | `summaries.rsvps.noCount`
      | `summaries.rsvps.waitlistCount`
      | `summaries.tickets.ticketsSold`
      | `summaries.tickets.revenue.amount`
      | `summaries.tickets.revenue.currency`
      | `summaries.tickets.currencyLocked`
      | `summaries.tickets.totalOrders`
      | `instanceId`
      | `guestListSettings.displayedPublicly`
      | `userId`
      | `onlineConferencing.enabled`
      | `onlineConferencing.type`
      | `onlineConferencing.session.hostLink`
      | `onlineConferencing.session.guestLink`
      | `seoSettings.slug`
      | `seoSettings.advancedSeoData.tags`
      | `seoSettings.advancedSeoData.tags.${number}.type`
      | `seoSettings.advancedSeoData.tags.${number}.children`
      | `seoSettings.advancedSeoData.tags.${number}.custom`
      | `seoSettings.advancedSeoData.tags.${number}.disabled`
      | `seoSettings.advancedSeoData.settings.preventAutoRedirect`
      | `seoSettings.advancedSeoData.settings.keywords`
      | `seoSettings.advancedSeoData.settings.keywords.${number}.term`
      | `seoSettings.advancedSeoData.settings.keywords.${number}.isMain`
      | `agendaSettings.enabled`,
      7
    > & {
      __applicationErrorsType?: UpdateEventApplicationErrors;
      __validationErrorsType?: UpdateEventValidationErrors;
    }
  >;
}

export function publishDraftEvent(
  httpClient: HttpClient
): PublishDraftEventSignature {
  return (eventId: string, options?: PublishDraftEventOptions) =>
    universalPublishDraftEvent(
      eventId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface PublishDraftEventSignature {
  /**
   * Publishes a draft event to your live site. Once published, the event's status changes from `"DRAFT"` to `"UPCOMING"`. <br> <br>
   * It's impossible to revert the `"DRAFT"` status after publishing. The only option is to clone the event, then delete the original.
   * @param - Event ID.
   * @param - Optional fields.
   */
  (eventId: string, options?: PublishDraftEventOptions): Promise<
    NonNullablePaths<
      PublishDraftEventResponse,
      | `event._id`
      | `event.location.type`
      | `event.location.address.streetAddress.number`
      | `event.location.address.streetAddress.name`
      | `event.dateAndTimeSettings.recurrenceStatus`
      | `event.dateAndTimeSettings.recurringEvents.individualEventDates`
      | `event.dateAndTimeSettings.recurringEvents.individualEventDates.${number}.showTimeZone`
      | `event.slug`
      | `event.status`
      | `event.registration.type`
      | `event.registration.status`
      | `event.registration.rsvp.responseType`
      | `event.registration.rsvp.waitlistEnabled`
      | `event.registration.tickets.guestsAssignedSeparately`
      | `event.registration.tickets.ticketLimitPerOrder`
      | `event.registration.tickets.lowestPrice.value`
      | `event.registration.tickets.lowestPrice.currency`
      | `event.registration.tickets.taxSettings.type`
      | `event.registration.allowedGuestTypes`
      | `event.registration.initialType`
      | `event.registration.registrationPaused`
      | `event.registration.registrationDisabled`
      | `event.calendarUrls.google`
      | `event.calendarUrls.ics`
      | `event.form.controls`
      | `event.form.controls.${number}.type`
      | `event.form.controls.${number}.system`
      | `event.form.controls.${number}.name`
      | `event.form.controls.${number}.label`
      | `event.form.controls.${number}.orderIndex`
      | `event.form.controls.${number}._id`
      | `event.form.messages.rsvp.rsvpYesOption`
      | `event.form.messages.rsvp.rsvpNoOption`
      | `event.form.messages.rsvp.positiveMessages.title`
      | `event.form.messages.rsvp.positiveMessages.confirmation.title`
      | `event.form.messages.rsvp.positiveMessages.confirmation.message`
      | `event.form.messages.rsvp.positiveMessages.confirmation.addToCalendarActionLabel`
      | `event.form.messages.rsvp.positiveMessages.confirmation.shareActionLabel`
      | `event.form.messages.rsvp.negativeMessages.title`
      | `event.form.messages.rsvp.negativeMessages.confirmation.title`
      | `event.form.messages.rsvp.negativeMessages.confirmation.shareActionLabel`
      | `event.form.messages.rsvp.submitActionLabel`
      | `event.form.messages.checkout.title`
      | `event.form.messages.checkout.submitActionLabel`
      | `event.form.messages.checkout.confirmation.title`
      | `event.form.messages.checkout.confirmation.message`
      | `event.form.messages.checkout.confirmation.downloadTicketsLabel`
      | `event.form.messages.checkout.confirmation.addToCalendarLabel`
      | `event.form.messages.checkout.confirmation.shareEventLabel`
      | `event.form.messages.registrationClosed.message`
      | `event.form.messages.registrationClosed.exploreEventsActionLabel`
      | `event.form.messages.ticketsUnavailable.message`
      | `event.form.messages.ticketsUnavailable.exploreEventsActionLabel`
      | `event.summaries.rsvps.totalCount`
      | `event.summaries.rsvps.yesCount`
      | `event.summaries.rsvps.noCount`
      | `event.summaries.rsvps.waitlistCount`
      | `event.summaries.tickets.ticketsSold`
      | `event.summaries.tickets.revenue.amount`
      | `event.summaries.tickets.revenue.currency`
      | `event.summaries.tickets.currencyLocked`
      | `event.summaries.tickets.totalOrders`
      | `event.instanceId`
      | `event.guestListSettings.displayedPublicly`
      | `event.userId`
      | `event.onlineConferencing.enabled`
      | `event.onlineConferencing.type`
      | `event.onlineConferencing.session.hostLink`
      | `event.onlineConferencing.session.guestLink`
      | `event.seoSettings.slug`
      | `event.seoSettings.advancedSeoData.tags`
      | `event.seoSettings.advancedSeoData.tags.${number}.type`
      | `event.seoSettings.advancedSeoData.tags.${number}.children`
      | `event.seoSettings.advancedSeoData.tags.${number}.custom`
      | `event.seoSettings.advancedSeoData.tags.${number}.disabled`
      | `event.seoSettings.advancedSeoData.settings.preventAutoRedirect`
      | `event.seoSettings.advancedSeoData.settings.keywords`
      | `event.seoSettings.advancedSeoData.settings.keywords.${number}.term`
      | `event.seoSettings.advancedSeoData.settings.keywords.${number}.isMain`
      | `event.agendaSettings.enabled`,
      8
    > & {
      __applicationErrorsType?: PublishDraftEventApplicationErrors;
    }
  >;
}

export function cancelEvent(httpClient: HttpClient): CancelEventSignature {
  return (eventId: string, options?: CancelEventOptions) =>
    universalCancelEvent(
      eventId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CancelEventSignature {
  /**
   * Cancels an event. <br> <br>
   * After cancellation, registration for an event is closed. To reuse the event, call Clone Event and Publish Event to publish it again. <br> <br>
   * If event cancellation notifications are enabled, canceling an event automatically triggers the sending of cancellation emails and/or push notifications to registered guests.
   * @param - Event ID.
   * @param - Optional fields.
   */
  (eventId: string, options?: CancelEventOptions): Promise<
    NonNullablePaths<
      CancelEventResponse,
      | `event._id`
      | `event.location.type`
      | `event.location.address.streetAddress.number`
      | `event.location.address.streetAddress.name`
      | `event.dateAndTimeSettings.recurrenceStatus`
      | `event.dateAndTimeSettings.recurringEvents.individualEventDates`
      | `event.dateAndTimeSettings.recurringEvents.individualEventDates.${number}.showTimeZone`
      | `event.slug`
      | `event.status`
      | `event.registration.type`
      | `event.registration.status`
      | `event.registration.rsvp.responseType`
      | `event.registration.rsvp.waitlistEnabled`
      | `event.registration.tickets.guestsAssignedSeparately`
      | `event.registration.tickets.ticketLimitPerOrder`
      | `event.registration.tickets.lowestPrice.value`
      | `event.registration.tickets.lowestPrice.currency`
      | `event.registration.tickets.taxSettings.type`
      | `event.registration.allowedGuestTypes`
      | `event.registration.initialType`
      | `event.registration.registrationPaused`
      | `event.registration.registrationDisabled`
      | `event.calendarUrls.google`
      | `event.calendarUrls.ics`
      | `event.form.controls`
      | `event.form.controls.${number}.type`
      | `event.form.controls.${number}.system`
      | `event.form.controls.${number}.name`
      | `event.form.controls.${number}.label`
      | `event.form.controls.${number}.orderIndex`
      | `event.form.controls.${number}._id`
      | `event.form.messages.rsvp.rsvpYesOption`
      | `event.form.messages.rsvp.rsvpNoOption`
      | `event.form.messages.rsvp.positiveMessages.title`
      | `event.form.messages.rsvp.positiveMessages.confirmation.title`
      | `event.form.messages.rsvp.positiveMessages.confirmation.message`
      | `event.form.messages.rsvp.positiveMessages.confirmation.addToCalendarActionLabel`
      | `event.form.messages.rsvp.positiveMessages.confirmation.shareActionLabel`
      | `event.form.messages.rsvp.negativeMessages.title`
      | `event.form.messages.rsvp.negativeMessages.confirmation.title`
      | `event.form.messages.rsvp.negativeMessages.confirmation.shareActionLabel`
      | `event.form.messages.rsvp.submitActionLabel`
      | `event.form.messages.checkout.title`
      | `event.form.messages.checkout.submitActionLabel`
      | `event.form.messages.checkout.confirmation.title`
      | `event.form.messages.checkout.confirmation.message`
      | `event.form.messages.checkout.confirmation.downloadTicketsLabel`
      | `event.form.messages.checkout.confirmation.addToCalendarLabel`
      | `event.form.messages.checkout.confirmation.shareEventLabel`
      | `event.form.messages.registrationClosed.message`
      | `event.form.messages.registrationClosed.exploreEventsActionLabel`
      | `event.form.messages.ticketsUnavailable.message`
      | `event.form.messages.ticketsUnavailable.exploreEventsActionLabel`
      | `event.summaries.rsvps.totalCount`
      | `event.summaries.rsvps.yesCount`
      | `event.summaries.rsvps.noCount`
      | `event.summaries.rsvps.waitlistCount`
      | `event.summaries.tickets.ticketsSold`
      | `event.summaries.tickets.revenue.amount`
      | `event.summaries.tickets.revenue.currency`
      | `event.summaries.tickets.currencyLocked`
      | `event.summaries.tickets.totalOrders`
      | `event.instanceId`
      | `event.guestListSettings.displayedPublicly`
      | `event.userId`
      | `event.onlineConferencing.enabled`
      | `event.onlineConferencing.type`
      | `event.onlineConferencing.session.hostLink`
      | `event.onlineConferencing.session.guestLink`
      | `event.seoSettings.slug`
      | `event.seoSettings.advancedSeoData.tags`
      | `event.seoSettings.advancedSeoData.tags.${number}.type`
      | `event.seoSettings.advancedSeoData.tags.${number}.children`
      | `event.seoSettings.advancedSeoData.tags.${number}.custom`
      | `event.seoSettings.advancedSeoData.tags.${number}.disabled`
      | `event.seoSettings.advancedSeoData.settings.preventAutoRedirect`
      | `event.seoSettings.advancedSeoData.settings.keywords`
      | `event.seoSettings.advancedSeoData.settings.keywords.${number}.term`
      | `event.seoSettings.advancedSeoData.settings.keywords.${number}.isMain`
      | `event.agendaSettings.enabled`,
      8
    > & {
      __applicationErrorsType?: CancelEventApplicationErrors;
    }
  >;
}

export function bulkCancelEventsByFilter(
  httpClient: HttpClient
): BulkCancelEventsByFilterSignature {
  return (
    options?: NonNullablePaths<
      BulkCancelEventsByFilterOptions,
      `filter.filter`,
      3
    >
  ) =>
    universalBulkCancelEventsByFilter(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCancelEventsByFilterSignature {
  /**
   * Cancels multiple events that meet the specified criteria. <br> <br>
   * After cancellation, registration for an event is closed. To reuse the event, call Clone Event and Publish Event to publish it again. <br> <br>
   * If event cancellation notifications are enabled, canceling an event automatically triggers the sending of cancellation emails and/or push notifications to registered guests.
   * @param - Optional fields.
   */
  (
    options?: NonNullablePaths<
      BulkCancelEventsByFilterOptions,
      `filter.filter`,
      3
    >
  ): Promise<void>;
}

export function deleteEvent(httpClient: HttpClient): DeleteEventSignature {
  return (eventId: string) =>
    universalDeleteEvent(
      eventId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteEventSignature {
  /**
   * Deletes an event. <br> <br>
   * You can retrieve the deleted event through a GDPR access request.
   * @param - Event ID.
   */
  (eventId: string): Promise<
    NonNullablePaths<DeleteEventResponse, `eventId`, 2>
  >;
}

export function bulkDeleteEventsByFilter(
  httpClient: HttpClient
): BulkDeleteEventsByFilterSignature {
  return (
    options?: NonNullablePaths<
      BulkDeleteEventsByFilterOptions,
      `filter.filter`,
      3
    >
  ) =>
    universalBulkDeleteEventsByFilter(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDeleteEventsByFilterSignature {
  /**
   * Deletes multiple events that meet the specified criteria. <br> <br>
   * You can retrieve the deleted events through a GDPR access request.
   * @param - Optional fields.
   */
  (
    options?: NonNullablePaths<
      BulkDeleteEventsByFilterOptions,
      `filter.filter`,
      3
    >
  ): Promise<void>;
}

export function queryEvents(httpClient: HttpClient): QueryEventsSignature {
  return (options?: QueryEventsOptions) =>
    universalQueryEvents(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryEventsSignature {
  /**
   * Creates a query to retrieve a list of events.
   *
   *
   * The `queryEvents()` method builds a query to retrieve a list of events and returns a [`EventsQueryBuilder`](https://www.wix.com/velo/reference/wix-events-v2/wixeventsv2/eventsquerybuilder) object.
   *
   * The returned object contains the query definition, which is typically used to run the query using the [`find()`](https://www.wix.com/velo/reference/wix-events-v2/wixeventsv2/eventsquerybuilder/find) method.
   *
   * You can refine the query by chaining `EventsQueryBuilder` methods onto the query. `EventsQueryBuilder` methods enable you to sort, filter, and control the results `queryEvents()` returns.
   *
   * `queryEvents()` runs with these `EventsQueryBuilder` defaults, which you can override:
   *
   * - [`skip(0)`](https://www.wix.com/velo/reference/wix-events-v2/wixeventsv2/eventsquerybuilder/skip)
   * - [`limit(50)`](https://www.wix.com/velo/reference/wix-events-v2/wixeventsv2/eventsquerybuilder/limit)
   * - [`descending("_createdDate")`](https://www.wix.com/velo/reference/wix-events-v2/wixeventsv2/eventsquerybuilder/descending)
   *
   * The methods that are chained to `queryEvents()` are applied in the order they're called. For example, if you apply `ascending('title')` and then `descending('status')`, the results are sorted first by the `title`, and then, if there are multiple results with the same `title`, the items are sorted by `status`.
   * @param - Optional fields.
   */
  (options?: QueryEventsOptions): EventsQueryBuilder;
}

export function typedQueryEvents(
  httpClient: HttpClient
): TypedQueryEventsSignature {
  return (query: EventQuery, options?: QueryEventsOptions) =>
    universalTypedQueryEvents(
      query,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryEventsSignature {
  /** */
  (query: EventQuery, options?: QueryEventsOptions): Promise<
    NonNullablePaths<
      QueryEventsResponse,
      | `events`
      | `events.${number}._id`
      | `events.${number}.location.type`
      | `events.${number}.dateAndTimeSettings.recurrenceStatus`
      | `events.${number}.slug`
      | `events.${number}.status`
      | `events.${number}.registration.type`
      | `events.${number}.registration.status`
      | `events.${number}.registration.rsvp.responseType`
      | `events.${number}.registration.rsvp.waitlistEnabled`
      | `events.${number}.registration.tickets.guestsAssignedSeparately`
      | `events.${number}.registration.tickets.ticketLimitPerOrder`
      | `events.${number}.registration.allowedGuestTypes`
      | `events.${number}.registration.initialType`
      | `events.${number}.registration.registrationPaused`
      | `events.${number}.registration.registrationDisabled`
      | `events.${number}.calendarUrls.google`
      | `events.${number}.calendarUrls.ics`
      | `events.${number}.summaries.rsvps.totalCount`
      | `events.${number}.summaries.rsvps.yesCount`
      | `events.${number}.summaries.rsvps.noCount`
      | `events.${number}.summaries.rsvps.waitlistCount`
      | `events.${number}.summaries.tickets.ticketsSold`
      | `events.${number}.summaries.tickets.currencyLocked`
      | `events.${number}.summaries.tickets.totalOrders`
      | `events.${number}.instanceId`
      | `events.${number}.guestListSettings.displayedPublicly`
      | `events.${number}.userId`
      | `events.${number}.onlineConferencing.enabled`
      | `events.${number}.onlineConferencing.type`
      | `events.${number}.onlineConferencing.session.hostLink`
      | `events.${number}.onlineConferencing.session.guestLink`
      | `events.${number}.seoSettings.slug`
      | `events.${number}.agendaSettings.enabled`,
      6
    > & {
      __applicationErrorsType?: QueryEventsApplicationErrors;
    }
  >;
}

export function countEventsByStatus(
  httpClient: HttpClient
): CountEventsByStatusSignature {
  return (options?: CountEventsByStatusOptions) =>
    universalCountEventsByStatus(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CountEventsByStatusSignature {
  /**
   * Counts events by status.
   * @param - Optional fields.
   */
  (options?: CountEventsByStatusOptions): Promise<
    CountEventsByStatusResponse & {
      __applicationErrorsType?: CountEventsByStatusApplicationErrors;
    }
  >;
}

export function listEventsByCategory(
  httpClient: HttpClient
): ListEventsByCategorySignature {
  return (categoryId: string, options?: ListEventsByCategoryOptions) =>
    universalListEventsByCategory(
      categoryId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListEventsByCategorySignature {
  /**
   * Retrieves a list of up to 100 events that belong to the same event category.
   * @param - Category ID.
   * @param - Optional fields.
   */
  (categoryId: string, options?: ListEventsByCategoryOptions): Promise<
    NonNullablePaths<
      ListEventsByCategoryResponse,
      | `events`
      | `events.${number}._id`
      | `events.${number}.location.type`
      | `events.${number}.dateAndTimeSettings.recurrenceStatus`
      | `events.${number}.slug`
      | `events.${number}.status`
      | `events.${number}.registration.type`
      | `events.${number}.registration.status`
      | `events.${number}.registration.rsvp.responseType`
      | `events.${number}.registration.rsvp.waitlistEnabled`
      | `events.${number}.registration.tickets.guestsAssignedSeparately`
      | `events.${number}.registration.tickets.ticketLimitPerOrder`
      | `events.${number}.registration.allowedGuestTypes`
      | `events.${number}.registration.initialType`
      | `events.${number}.registration.registrationPaused`
      | `events.${number}.registration.registrationDisabled`
      | `events.${number}.calendarUrls.google`
      | `events.${number}.calendarUrls.ics`
      | `events.${number}.summaries.rsvps.totalCount`
      | `events.${number}.summaries.rsvps.yesCount`
      | `events.${number}.summaries.rsvps.noCount`
      | `events.${number}.summaries.rsvps.waitlistCount`
      | `events.${number}.summaries.tickets.ticketsSold`
      | `events.${number}.summaries.tickets.currencyLocked`
      | `events.${number}.summaries.tickets.totalOrders`
      | `events.${number}.instanceId`
      | `events.${number}.guestListSettings.displayedPublicly`
      | `events.${number}.userId`
      | `events.${number}.onlineConferencing.enabled`
      | `events.${number}.onlineConferencing.type`
      | `events.${number}.onlineConferencing.session.hostLink`
      | `events.${number}.onlineConferencing.session.guestLink`
      | `events.${number}.seoSettings.slug`
      | `events.${number}.agendaSettings.enabled`,
      6
    >
  >;
}

export function getEvent(httpClient: HttpClient): GetEventSignature {
  return (eventId: string, options?: GetEventOptions) =>
    universalGetEvent(
      eventId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetEventSignature {
  /**
   * Retrieves an event by ID.
   * @param - Event ID.
   * @param - Optional fields.
   * @returns Event.
   */
  (eventId: string, options?: GetEventOptions): Promise<
    NonNullablePaths<
      Event,
      | `_id`
      | `location.type`
      | `location.address.streetAddress.number`
      | `location.address.streetAddress.name`
      | `dateAndTimeSettings.recurrenceStatus`
      | `dateAndTimeSettings.recurringEvents.individualEventDates`
      | `dateAndTimeSettings.recurringEvents.individualEventDates.${number}.showTimeZone`
      | `slug`
      | `status`
      | `registration.type`
      | `registration.status`
      | `registration.rsvp.responseType`
      | `registration.rsvp.waitlistEnabled`
      | `registration.tickets.guestsAssignedSeparately`
      | `registration.tickets.ticketLimitPerOrder`
      | `registration.tickets.lowestPrice.value`
      | `registration.tickets.lowestPrice.currency`
      | `registration.tickets.taxSettings.type`
      | `registration.allowedGuestTypes`
      | `registration.initialType`
      | `registration.registrationPaused`
      | `registration.registrationDisabled`
      | `calendarUrls.google`
      | `calendarUrls.ics`
      | `form.controls`
      | `form.controls.${number}.type`
      | `form.controls.${number}.system`
      | `form.controls.${number}.name`
      | `form.controls.${number}.label`
      | `form.controls.${number}.orderIndex`
      | `form.controls.${number}._id`
      | `form.messages.rsvp.rsvpYesOption`
      | `form.messages.rsvp.rsvpNoOption`
      | `form.messages.rsvp.positiveMessages.title`
      | `form.messages.rsvp.positiveMessages.confirmation.title`
      | `form.messages.rsvp.positiveMessages.confirmation.message`
      | `form.messages.rsvp.positiveMessages.confirmation.addToCalendarActionLabel`
      | `form.messages.rsvp.positiveMessages.confirmation.shareActionLabel`
      | `form.messages.rsvp.negativeMessages.title`
      | `form.messages.rsvp.negativeMessages.confirmation.title`
      | `form.messages.rsvp.negativeMessages.confirmation.shareActionLabel`
      | `form.messages.rsvp.submitActionLabel`
      | `form.messages.checkout.title`
      | `form.messages.checkout.submitActionLabel`
      | `form.messages.checkout.confirmation.title`
      | `form.messages.checkout.confirmation.message`
      | `form.messages.checkout.confirmation.downloadTicketsLabel`
      | `form.messages.checkout.confirmation.addToCalendarLabel`
      | `form.messages.checkout.confirmation.shareEventLabel`
      | `form.messages.registrationClosed.message`
      | `form.messages.registrationClosed.exploreEventsActionLabel`
      | `form.messages.ticketsUnavailable.message`
      | `form.messages.ticketsUnavailable.exploreEventsActionLabel`
      | `summaries.rsvps.totalCount`
      | `summaries.rsvps.yesCount`
      | `summaries.rsvps.noCount`
      | `summaries.rsvps.waitlistCount`
      | `summaries.tickets.ticketsSold`
      | `summaries.tickets.revenue.amount`
      | `summaries.tickets.revenue.currency`
      | `summaries.tickets.currencyLocked`
      | `summaries.tickets.totalOrders`
      | `instanceId`
      | `guestListSettings.displayedPublicly`
      | `userId`
      | `onlineConferencing.enabled`
      | `onlineConferencing.type`
      | `onlineConferencing.session.hostLink`
      | `onlineConferencing.session.guestLink`
      | `seoSettings.slug`
      | `seoSettings.advancedSeoData.tags`
      | `seoSettings.advancedSeoData.tags.${number}.type`
      | `seoSettings.advancedSeoData.tags.${number}.children`
      | `seoSettings.advancedSeoData.tags.${number}.custom`
      | `seoSettings.advancedSeoData.tags.${number}.disabled`
      | `seoSettings.advancedSeoData.settings.preventAutoRedirect`
      | `seoSettings.advancedSeoData.settings.keywords`
      | `seoSettings.advancedSeoData.settings.keywords.${number}.term`
      | `seoSettings.advancedSeoData.settings.keywords.${number}.isMain`
      | `agendaSettings.enabled`,
      7
    >
  >;
}

export function getEventBySlug(
  httpClient: HttpClient
): GetEventBySlugSignature {
  return (slug: string, options?: GetEventBySlugOptions) =>
    universalGetEventBySlug(
      slug,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetEventBySlugSignature {
  /**
   * Retrieves an event by the slug URL.
   *
   * The slug is the end of an event URL that refers to a specific event. For example, if an event's URL is `https://example.com/events/event/{my-event-slug}`, the slug is `my-event-slug`.
   * @param - URL slug.
   * @param - Optional fields.
   */
  (slug: string, options?: GetEventBySlugOptions): Promise<
    NonNullablePaths<
      GetEventBySlugResponse,
      | `event._id`
      | `event.location.type`
      | `event.location.address.streetAddress.number`
      | `event.location.address.streetAddress.name`
      | `event.dateAndTimeSettings.recurrenceStatus`
      | `event.dateAndTimeSettings.recurringEvents.individualEventDates`
      | `event.dateAndTimeSettings.recurringEvents.individualEventDates.${number}.showTimeZone`
      | `event.slug`
      | `event.status`
      | `event.registration.type`
      | `event.registration.status`
      | `event.registration.rsvp.responseType`
      | `event.registration.rsvp.waitlistEnabled`
      | `event.registration.tickets.guestsAssignedSeparately`
      | `event.registration.tickets.ticketLimitPerOrder`
      | `event.registration.tickets.lowestPrice.value`
      | `event.registration.tickets.lowestPrice.currency`
      | `event.registration.tickets.taxSettings.type`
      | `event.registration.allowedGuestTypes`
      | `event.registration.initialType`
      | `event.registration.registrationPaused`
      | `event.registration.registrationDisabled`
      | `event.calendarUrls.google`
      | `event.calendarUrls.ics`
      | `event.form.controls`
      | `event.form.controls.${number}.type`
      | `event.form.controls.${number}.system`
      | `event.form.controls.${number}.name`
      | `event.form.controls.${number}.label`
      | `event.form.controls.${number}.orderIndex`
      | `event.form.controls.${number}._id`
      | `event.form.messages.rsvp.rsvpYesOption`
      | `event.form.messages.rsvp.rsvpNoOption`
      | `event.form.messages.rsvp.positiveMessages.title`
      | `event.form.messages.rsvp.positiveMessages.confirmation.title`
      | `event.form.messages.rsvp.positiveMessages.confirmation.message`
      | `event.form.messages.rsvp.positiveMessages.confirmation.addToCalendarActionLabel`
      | `event.form.messages.rsvp.positiveMessages.confirmation.shareActionLabel`
      | `event.form.messages.rsvp.negativeMessages.title`
      | `event.form.messages.rsvp.negativeMessages.confirmation.title`
      | `event.form.messages.rsvp.negativeMessages.confirmation.shareActionLabel`
      | `event.form.messages.rsvp.submitActionLabel`
      | `event.form.messages.checkout.title`
      | `event.form.messages.checkout.submitActionLabel`
      | `event.form.messages.checkout.confirmation.title`
      | `event.form.messages.checkout.confirmation.message`
      | `event.form.messages.checkout.confirmation.downloadTicketsLabel`
      | `event.form.messages.checkout.confirmation.addToCalendarLabel`
      | `event.form.messages.checkout.confirmation.shareEventLabel`
      | `event.form.messages.registrationClosed.message`
      | `event.form.messages.registrationClosed.exploreEventsActionLabel`
      | `event.form.messages.ticketsUnavailable.message`
      | `event.form.messages.ticketsUnavailable.exploreEventsActionLabel`
      | `event.summaries.rsvps.totalCount`
      | `event.summaries.rsvps.yesCount`
      | `event.summaries.rsvps.noCount`
      | `event.summaries.rsvps.waitlistCount`
      | `event.summaries.tickets.ticketsSold`
      | `event.summaries.tickets.revenue.amount`
      | `event.summaries.tickets.revenue.currency`
      | `event.summaries.tickets.currencyLocked`
      | `event.summaries.tickets.totalOrders`
      | `event.instanceId`
      | `event.guestListSettings.displayedPublicly`
      | `event.userId`
      | `event.onlineConferencing.enabled`
      | `event.onlineConferencing.type`
      | `event.onlineConferencing.session.hostLink`
      | `event.onlineConferencing.session.guestLink`
      | `event.seoSettings.slug`
      | `event.seoSettings.advancedSeoData.tags`
      | `event.seoSettings.advancedSeoData.tags.${number}.type`
      | `event.seoSettings.advancedSeoData.tags.${number}.children`
      | `event.seoSettings.advancedSeoData.tags.${number}.custom`
      | `event.seoSettings.advancedSeoData.tags.${number}.disabled`
      | `event.seoSettings.advancedSeoData.settings.preventAutoRedirect`
      | `event.seoSettings.advancedSeoData.settings.keywords`
      | `event.seoSettings.advancedSeoData.settings.keywords.${number}.term`
      | `event.seoSettings.advancedSeoData.settings.keywords.${number}.isMain`
      | `event.agendaSettings.enabled`,
      8
    >
  >;
}

export const onEventCanceled = EventDefinition(
  'wix.events.v3.event_canceled',
  true,
  (event: EventCanceledEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'data.timestamp' }, { path: 'metadata.eventTime' }],
        },
      ])
    )
)<EventCanceledEnvelope>();
export const onEventCloned = EventDefinition(
  'wix.events.v3.event_cloned',
  true,
  (event: EventClonedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.timestamp' },
            { path: 'data.dateAndTimeSettings.startDate' },
            { path: 'data.dateAndTimeSettings.endDate' },
            {
              path: 'data.dateAndTimeSettings.recurringEvents.individualEventDates.startDate',
            },
            {
              path: 'data.dateAndTimeSettings.recurringEvents.individualEventDates.endDate',
            },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'data.location.address' }],
        },
      ])
    )
)<EventClonedEnvelope>();
export const onEventCreated = EventDefinition(
  'wix.events.v3.event_created',
  true,
  (event: EventCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'entity.mainImage' }],
        },
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.publishedDate' },
            { path: 'entity.dateAndTimeSettings.startDate' },
            { path: 'entity.dateAndTimeSettings.endDate' },
            {
              path: 'entity.dateAndTimeSettings.recurringEvents.individualEventDates.startDate',
            },
            {
              path: 'entity.dateAndTimeSettings.recurringEvents.individualEventDates.endDate',
            },
            { path: 'entity.registration.rsvp.startDate' },
            { path: 'entity.registration.rsvp.endDate' },
            { path: 'entity.categories.categories.createdDate' },
            { path: 'entity.description.metadata.createdTimestamp' },
            { path: 'entity.description.metadata.updatedTimestamp' },
            { path: 'entity.badges.badges.functionUpdateDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [
            { path: 'entity.eventPageUrl' },
            { path: 'entity.agendaSettings.pageUrl' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'entity.location.address' }],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            {
              path: 'entity.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            { path: 'entity.description.nodes.galleryData.options.item.ratio' },
            { path: 'entity.description.nodes.imageData.image.duration' },
            { path: 'entity.description.nodes.mapData.mapSettings.lat' },
            { path: 'entity.description.nodes.mapData.mapSettings.lng' },
            { path: 'entity.description.nodes.pollData.poll.image.duration' },
            {
              path: 'entity.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.description.nodes.pollData.design.poll.background.image.duration',
            },
            { path: 'entity.description.nodes.appEmbedData.image.duration' },
            { path: 'entity.description.nodes.videoData.video.duration' },
            { path: 'entity.description.nodes.videoData.thumbnail.duration' },
            { path: 'entity.description.nodes.audioData.audio.duration' },
            { path: 'entity.description.nodes.audioData.coverImage.duration' },
            {
              path: 'entity.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            { path: 'entity.description.nodes.shapeData.shape.duration' },
            {
              path: 'entity.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.description.nodes.cardData.backgroundImage.media.duration',
            },
            { path: 'entity.description.nodes.tocData.fontSize' },
            { path: 'entity.description.nodes.tocData.itemSpacing' },
            {
              path: 'entity.description.nodes.smartBlockCellData.shape.duration',
            },
          ],
        },
      ])
    )
)<EventCreatedEnvelope>();
export const onEventDeleted = EventDefinition(
  'wix.events.v3.event_deleted',
  true,
  (event: EventDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'undefined.mainImage' }],
        },
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'undefined.createdDate' },
            { path: 'undefined.updatedDate' },
            { path: 'undefined.publishedDate' },
            { path: 'undefined.dateAndTimeSettings.startDate' },
            { path: 'undefined.dateAndTimeSettings.endDate' },
            {
              path: 'undefined.dateAndTimeSettings.recurringEvents.individualEventDates.startDate',
            },
            {
              path: 'undefined.dateAndTimeSettings.recurringEvents.individualEventDates.endDate',
            },
            { path: 'undefined.registration.rsvp.startDate' },
            { path: 'undefined.registration.rsvp.endDate' },
            { path: 'undefined.categories.categories.createdDate' },
            { path: 'undefined.description.metadata.createdTimestamp' },
            { path: 'undefined.description.metadata.updatedTimestamp' },
            { path: 'undefined.badges.badges.functionUpdateDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [
            { path: 'undefined.eventPageUrl' },
            { path: 'undefined.agendaSettings.pageUrl' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'undefined.location.address' }],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            {
              path: 'undefined.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'undefined.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'undefined.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'undefined.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'undefined.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'undefined.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'undefined.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'undefined.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'undefined.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            {
              path: 'undefined.description.nodes.galleryData.options.item.ratio',
            },
            { path: 'undefined.description.nodes.imageData.image.duration' },
            { path: 'undefined.description.nodes.mapData.mapSettings.lat' },
            { path: 'undefined.description.nodes.mapData.mapSettings.lng' },
            {
              path: 'undefined.description.nodes.pollData.poll.image.duration',
            },
            {
              path: 'undefined.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'undefined.description.nodes.pollData.design.poll.background.image.duration',
            },
            { path: 'undefined.description.nodes.appEmbedData.image.duration' },
            { path: 'undefined.description.nodes.videoData.video.duration' },
            {
              path: 'undefined.description.nodes.videoData.thumbnail.duration',
            },
            { path: 'undefined.description.nodes.audioData.audio.duration' },
            {
              path: 'undefined.description.nodes.audioData.coverImage.duration',
            },
            {
              path: 'undefined.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'undefined.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'undefined.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'undefined.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'undefined.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'undefined.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'undefined.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'undefined.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            { path: 'undefined.description.nodes.shapeData.shape.duration' },
            {
              path: 'undefined.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'undefined.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'undefined.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'undefined.description.nodes.cardData.backgroundImage.media.duration',
            },
            { path: 'undefined.description.nodes.tocData.fontSize' },
            { path: 'undefined.description.nodes.tocData.itemSpacing' },
            {
              path: 'undefined.description.nodes.smartBlockCellData.shape.duration',
            },
          ],
        },
      ])
    )
)<EventDeletedEnvelope>();
export const onEventEnded = EventDefinition(
  'wix.events.v3.event_ended',
  true,
  (event: EventEndedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'data.timestamp' }, { path: 'metadata.eventTime' }],
        },
      ])
    )
)<EventEndedEnvelope>();
export const onEventPublished = EventDefinition(
  'wix.events.v3.event_published',
  true,
  (event: EventPublishedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.timestamp' },
            { path: 'data.dateAndTimeSettings.startDate' },
            { path: 'data.dateAndTimeSettings.endDate' },
            {
              path: 'data.dateAndTimeSettings.recurringEvents.individualEventDates.startDate',
            },
            {
              path: 'data.dateAndTimeSettings.recurringEvents.individualEventDates.endDate',
            },
            { path: 'data.registration.rsvp.startDate' },
            { path: 'data.registration.rsvp.endDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [{ path: 'data.eventPageUrl' }],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'data.location.address' }],
        },
      ])
    )
)<EventPublishedEnvelope>();
export const onEventReminder = EventDefinition(
  'wix.events.v3.event_reminder',
  true,
  (event: EventReminderEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.timestamp' },
            { path: 'data.dateAndTimeSettings.startDate' },
            { path: 'data.dateAndTimeSettings.endDate' },
            {
              path: 'data.dateAndTimeSettings.recurringEvents.individualEventDates.startDate',
            },
            {
              path: 'data.dateAndTimeSettings.recurringEvents.individualEventDates.endDate',
            },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'data.location.address' }],
        },
      ])
    )
)<EventReminderEnvelope>();
export const onEventStarted = EventDefinition(
  'wix.events.v3.event_started',
  true,
  (event: EventStartedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'data.timestamp' }, { path: 'metadata.eventTime' }],
        },
      ])
    )
)<EventStartedEnvelope>();
export const onEventUpdated = EventDefinition(
  'wix.events.v3.event_updated',
  true,
  (event: EventUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'entity.mainImage' }],
        },
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.publishedDate' },
            { path: 'entity.dateAndTimeSettings.startDate' },
            { path: 'entity.dateAndTimeSettings.endDate' },
            {
              path: 'entity.dateAndTimeSettings.recurringEvents.individualEventDates.startDate',
            },
            {
              path: 'entity.dateAndTimeSettings.recurringEvents.individualEventDates.endDate',
            },
            { path: 'entity.registration.rsvp.startDate' },
            { path: 'entity.registration.rsvp.endDate' },
            { path: 'entity.categories.categories.createdDate' },
            { path: 'entity.description.metadata.createdTimestamp' },
            { path: 'entity.description.metadata.updatedTimestamp' },
            { path: 'entity.badges.badges.functionUpdateDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTPageURLToSDKPageURL,
          paths: [
            { path: 'entity.eventPageUrl' },
            { path: 'entity.agendaSettings.pageUrl' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'entity.location.address' }],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            {
              path: 'entity.description.nodes.buttonData.styles.background.gradient.centerX',
            },
            {
              path: 'entity.description.nodes.buttonData.styles.background.gradient.centerY',
            },
            {
              path: 'entity.description.nodes.buttonData.styles.background.gradient.stops.position',
            },
            {
              path: 'entity.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
            },
            {
              path: 'entity.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
            },
            {
              path: 'entity.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
            },
            {
              path: 'entity.description.nodes.galleryData.items.image.media.duration',
            },
            {
              path: 'entity.description.nodes.galleryData.items.video.media.duration',
            },
            {
              path: 'entity.description.nodes.galleryData.items.video.thumbnail.duration',
            },
            { path: 'entity.description.nodes.galleryData.options.item.ratio' },
            { path: 'entity.description.nodes.imageData.image.duration' },
            { path: 'entity.description.nodes.mapData.mapSettings.lat' },
            { path: 'entity.description.nodes.mapData.mapSettings.lng' },
            { path: 'entity.description.nodes.pollData.poll.image.duration' },
            {
              path: 'entity.description.nodes.pollData.poll.options.image.duration',
            },
            {
              path: 'entity.description.nodes.pollData.design.poll.background.image.duration',
            },
            { path: 'entity.description.nodes.appEmbedData.image.duration' },
            { path: 'entity.description.nodes.videoData.video.duration' },
            { path: 'entity.description.nodes.videoData.thumbnail.duration' },
            { path: 'entity.description.nodes.audioData.audio.duration' },
            { path: 'entity.description.nodes.audioData.coverImage.duration' },
            {
              path: 'entity.description.nodes.layoutData.backgroundImage.media.duration',
            },
            {
              path: 'entity.description.nodes.layoutData.backdropImage.media.duration',
            },
            {
              path: 'entity.description.nodes.layoutData.background.gradient.centerX',
            },
            {
              path: 'entity.description.nodes.layoutData.background.gradient.centerY',
            },
            {
              path: 'entity.description.nodes.layoutData.background.gradient.stops.position',
            },
            {
              path: 'entity.description.nodes.layoutData.backdrop.gradient.centerX',
            },
            {
              path: 'entity.description.nodes.layoutData.backdrop.gradient.centerY',
            },
            {
              path: 'entity.description.nodes.layoutData.backdrop.gradient.stops.position',
            },
            { path: 'entity.description.nodes.shapeData.shape.duration' },
            {
              path: 'entity.description.nodes.cardData.background.gradient.centerX',
            },
            {
              path: 'entity.description.nodes.cardData.background.gradient.centerY',
            },
            {
              path: 'entity.description.nodes.cardData.background.gradient.stops.position',
            },
            {
              path: 'entity.description.nodes.cardData.backgroundImage.media.duration',
            },
            { path: 'entity.description.nodes.tocData.fontSize' },
            { path: 'entity.description.nodes.tocData.itemSpacing' },
            {
              path: 'entity.description.nodes.smartBlockCellData.shape.duration',
            },
          ],
        },
      ])
    )
)<EventUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  Address,
  AddressLocation,
  AddressStreetOneOf,
  AgendaSettings,
  Alignment,
  AnchorData,
  AppEmbedData,
  AppEmbedDataAppDataOneOf,
  AppType,
  AspectRatio,
  AudioData,
  Backdrop,
  BackdropType,
  Background,
  BackgroundGradient,
  BackgroundImage,
  BackgroundType,
  Badge,
  BadgeList,
  BadgeTypeType,
  Banner,
  BaseEventMetadata,
  BlockquoteData,
  BookingData,
  Border,
  BorderColors,
  BorderWidths,
  BulkCancelEventsByFilterOptions,
  BulkCancelEventsByFilterRequest,
  BulkCancelEventsByFilterResponse,
  BulkDeleteEventsByFilterOptions,
  BulkDeleteEventsByFilterRequest,
  BulkDeleteEventsByFilterResponse,
  BulletedListData,
  ButtonData,
  ButtonDataType,
  ButtonStyles,
  CalendarUrls,
  CancelEventOptions,
  CancelEventRequest,
  CancelEventResponse,
  CaptionData,
  CardData,
  CardDataBackground,
  CardDataBackgroundType,
  CardStyles,
  CardStylesType,
  CellStyle,
  CheckoutFormMessages,
  CheckoutType,
  CloneEventFromOtherSiteRequest,
  CloneEventFromOtherSiteResponse,
  CloneEventOptions,
  CloneEventRequest,
  CloneEventResponse,
  CodeBlockData,
  CollapsibleListData,
  ColorData,
  Colors,
  ColumnSize,
  CommonCursors,
  CommonPaging,
  CommonPagingMetadataV2,
  CommonQueryV2,
  CommonQueryV2PagingMethodOneOf,
  CommonSortOrder,
  CommonSorting,
  ConferenceTypeType,
  CountEventsByStatusOptions,
  CountEventsByStatusRequest,
  CountEventsByStatusResponse,
  CreateEventOptions,
  CreateEventRequest,
  CreateEventResponse,
  Crop,
  CursorPaging,
  Cursors,
  DateAndTimeSettings,
  Decoration,
  DecorationDataOneOf,
  DecorationType,
  DeleteEventRequest,
  DeleteEventResponse,
  Design,
  DesignTarget,
  Dimensions,
  Direction,
  DividerData,
  DividerDataAlignment,
  DocumentStyle,
  DomainEvent,
  DomainEventBodyOneOf,
  EmbedData,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  Event,
  EventCanceled,
  EventCanceledEnvelope,
  EventCategories,
  EventCategory,
  EventCloned,
  EventClonedEnvelope,
  EventCreatedEnvelope,
  EventData,
  EventDeletedEnvelope,
  EventDisplaySettings,
  EventEnded,
  EventEndedEnvelope,
  EventMetadata,
  EventPublished,
  EventPublishedEnvelope,
  EventQuerySpec,
  EventReminder,
  EventReminderEnvelope,
  EventStarted,
  EventStartedEnvelope,
  EventUpdatedEnvelope,
  EventsMoney,
  EventsQueryBuilder,
  EventsQueryResult,
  ExportEventsRequest,
  ExportEventsResponse,
  ExternalRegistration,
  FacetCounts,
  Feed,
  FileData,
  FileSource,
  FileSourceDataOneOf,
  FindEventRequest,
  FindEventRequestFindByOneOf,
  FindEventResponse,
  FontFamilyData,
  FontSizeData,
  FontType,
  Form,
  FormMessages,
  Formatted,
  GIF,
  GIFData,
  GIFType,
  GalleryData,
  GalleryOptions,
  GalleryOptionsLayout,
  GetEventBySlugOptions,
  GetEventBySlugRequest,
  GetEventBySlugResponse,
  GetEventOptions,
  GetEventRequest,
  GetEventResponse,
  Gradient,
  GradientType,
  GuestListSettings,
  GuestTypeType,
  HTMLData,
  HTMLDataDataOneOf,
  HeadingData,
  Height,
  IdentificationData,
  IdentificationDataIdOneOf,
  Image,
  ImageData,
  ImageDataStyles,
  ImagePosition,
  ImagePositionPosition,
  ImageScalingScaling,
  ImageStyles,
  Indentation,
  InitialExpandedItems,
  InitialRegistrationTypeType,
  Input,
  InputControl,
  InputControlType,
  Item,
  ItemDataOneOf,
  ItemStyle,
  Keyword,
  Label,
  LabellingSettings,
  Layout,
  LayoutCellData,
  LayoutData,
  LayoutDataBackground,
  LayoutDataBackgroundImage,
  LayoutDataBackgroundType,
  LayoutType,
  LineStyle,
  Link,
  LinkData,
  LinkDataOneOf,
  LinkPreviewData,
  LinkPreviewDataStyles,
  ListEventsByCategoryOptions,
  ListEventsByCategoryRequest,
  ListEventsByCategoryResponse,
  ListStyle,
  ListUserEventsRequest,
  ListUserEventsResponse,
  ListValue,
  Location,
  LocationType,
  MapData,
  MapSettings,
  MapType,
  Media,
  MentionData,
  MessageEnvelope,
  Metadata,
  Money,
  Negative,
  NegativeResponseConfirmation,
  Node,
  NodeDataOneOf,
  NodeStyle,
  NodeType,
  NullValue,
  Occurrence,
  Oembed,
  OnlineConferencing,
  OnlineConferencingSession,
  Option,
  OptionDesign,
  OptionLayout,
  OptionSelection,
  OptionSelectionSelectedOptionOneOf,
  OrderedListData,
  Orientation,
  Origin,
  PDFSettings,
  Paging,
  PagingMetadataV2,
  ParagraphData,
  Permissions,
  Placement,
  PlaybackOptions,
  PluginContainerData,
  PluginContainerDataAlignment,
  PluginContainerDataWidth,
  PluginContainerDataWidthDataOneOf,
  Poll,
  PollData,
  PollDataLayout,
  PollDesign,
  PollDesignBackground,
  PollDesignBackgroundBackgroundOneOf,
  PollDesignBackgroundType,
  PollLayout,
  PollLayoutDirection,
  PollLayoutType,
  PollSettings,
  Position,
  Positive,
  PositiveResponseConfirmation,
  PricingData,
  PublishDraftEventOptions,
  PublishDraftEventRequest,
  PublishDraftEventResponse,
  QueryEventsOptions,
  QueryEventsRequest,
  QueryEventsResponse,
  QueryV2,
  QueryV2PagingMethodOneOf,
  RecurrenceStatusStatus,
  Recurrences,
  Registration,
  RegistrationClosedMessages,
  RegistrationStatusStatus,
  RegistrationTypeType,
  Rel,
  Relation,
  RequestedFields,
  Resizing,
  ResponseConfirmation,
  ResponseType,
  ResponsivenessBehaviour,
  RestoreInfo,
  RibbonStyles,
  RichContent,
  RsvpConfirmationMessages,
  RsvpFormMessages,
  RsvpFormMessagesNegativeResponseConfirmation,
  RsvpFormMessagesPositiveResponseConfirmation,
  RsvpRegistration,
  Rsvps,
  Scaling,
  SeoSchema,
  SeoSettings,
  Settings,
  ShapeData,
  ShapeDataStyles,
  SmartBlockCellData,
  SmartBlockData,
  SmartBlockDataType,
  SortOrder,
  Sorting,
  Source,
  Spoiler,
  SpoilerData,
  Status,
  Stop,
  StreetAddress,
  Styles,
  StylesBorder,
  StylesPosition,
  Subdivision,
  SubdivisionType,
  Summaries,
  TableCellData,
  TableData,
  Tag,
  Target,
  TaxSettings,
  TaxType,
  TextAlignment,
  TextData,
  TextNodeStyle,
  TextStyle,
  Thumbnails,
  ThumbnailsAlignment,
  Tickets,
  TicketsConfirmationMessages,
  TicketsRegistration,
  TicketsUnavailableMessages,
  TimeDuration,
  TocData,
  Type,
  UpdateEventOptions,
  UpdateEventRequest,
  UpdateEventResponse,
  UserFilter,
  ValueType,
  VerticalAlignment,
  VerticalAlignmentAlignment,
  Video,
  VideoData,
  ViewMode,
  ViewRole,
  VoteRole,
  WebhookIdentityType,
  Width,
  WidthType,
  utils,
} from './events-v3-event-wix-events-v-2.universal.js';
